import React, { memo, useMemo, useState } from 'react'
import { Button, Checkbox, Dropdown, Flex, notification, Popconfirm, Popover, Switch, Tag, Typography } from 'antd'
import type { MenuProps } from 'antd'
import { MoreOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { TimeAgo } from '../../components/time-ago'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { useCrawlRuleSettings, useCrawlSettings, useSite, useUpdateCrawlSettings } from '../hooks'
import { SiteDocument } from '../site'

/**
 * The folder a page is in, such as "/vacatures/" for "/vacatures/verpleegkundige/". Null for a page at the top level,
 * which has no folder to choose.
 */
export function folderOf(url: string): string | null {
    const segments = new URL(url).pathname.split('/').filter(Boolean)

    return segments.length > 1 ? `/${segments.slice(0, -1).join('/')}/` : null
}

/**
 * The top folder a page is grouped under on the Knowledge page, such as "/blog/" for "/blog/" and "/blog/2024/opening".
 * Null for the home page.
 */
export function topFolderOf(url: string): string | null {
    const segment = new URL(url).pathname.split('/').filter(Boolean)[0]

    return segment ? `/${segment}/` : null
}

/** The rule for only this page: its path without a trailing slash, which the backend matches with or without one. */
function pageRule(url: string): string {
    return new URL(url).pathname.replace(/\/+$/, '') || '/'
}

/** The rule for every page in a folder, also pages Evaa finds later. */
function folderRule(folder: string): string {
    return `${folder}*`
}

/** Whether a rule is about pages in a folder: the folder's own page, a page in it, or (part of) the folder itself. */
function inFolder(rule: string, folder: string): boolean {
    return rule === folder.replace(/\/$/, '') || rule.startsWith(folder)
}

/** The folder of a rule like "/vacatures/*", or null for a rule for one page or one typed by hand. */
function ruleFolder(rule: string): string | null {
    const isFolderRule = rule.endsWith('/*') && rule.indexOf('*') === rule.length - 1

    return isFolderRule ? rule.slice(0, -1) : null
}

/**
 * Whether pages can be marked as a vacancy or not here: only for websites Evaa reads itself (with the snippet), as the
 * plugin decides that for WordPress. Skipping pages works for every website.
 */
export function useCanMarkVacancies(siteId: string): boolean {
    const { can } = useAuth()
    const { data: site } = useSite(siteId)

    return site?.type === 'snippet' && can(UPDATE_SITE)
}

type RuleList = 'vacancyPaths' | 'notVacancyPaths' | 'exclude'

export type CrawlRules = ReturnType<typeof useCrawlRules>

/**
 * The rules of a website, with changes that add or remove one rule and save all of them. Used once for the whole table
 * and handed to its rows, and the same object as long as the rules do not change, so rows only render again when theirs
 * change.
 */
export function useCrawlRules(siteId: string) {
    const { t } = useTranslation()
    const { data: settings } = useCrawlRuleSettings(siteId)
    const { mutate } = useUpdateCrawlSettings(siteId)

    return useMemo(() => {
        const save = (list: RuleList, change: (rules: string[]) => string[], message: string) => {
            if (!settings) {
                return
            }

            mutate(
                {
                    sitemapUrl: settings.sitemapUrl,
                    exclude: settings.exclude,
                    vacancyPaths: settings.vacancyPaths,
                    notVacancyPaths: settings.notVacancyPaths,
                    [list]: change(settings[list]),
                },
                { onSuccess: () => notification.success({ message }) }
            )
        }

        const add = (list: RuleList, message: string) => (rule: string) =>
            save(list, (rules) => [...rules.filter((path) => path !== rule), rule], message)

        const remove = (list: RuleList, message: string) => (rule: string) =>
            save(list, (rules) => rules.filter((path) => path !== rule), message)

        const readAgain = t('Saved. Evaa reads these pages again in a moment.')
        const skips = t('Evaa skips these pages from now on.')

        // One rule for the folder instead of the rules for pages in it, which it covers.
        const skipFolder = (folder: string) =>
            save('exclude', (rules) => [...rules.filter((rule) => !inFolder(rule, folder)), folderRule(folder)], skips)

        const readFolder = (folder: string) =>
            save('exclude', (rules) => rules.filter((rule) => !inFolder(rule, folder)), readAgain)

        return {
            settings,
            addVacancy: add('vacancyPaths', readAgain),
            removeVacancy: remove('vacancyPaths', readAgain),
            addNotVacancy: add('notVacancyPaths', readAgain),
            removeNotVacancy: remove('notVacancyPaths', readAgain),
            skipPage: (url: string) => add('exclude', skips)(pageRule(url)),
            skipFolder,
            readFolder,
            removeSkip: remove('exclude', readAgain),
        }
    }, [settings, mutate, t])
}

/** How a rule reads: "All pages in /vacatures/", the title of the page, or the rule as it was typed. */
function useRuleLabel(documents: SiteDocument[]) {
    const { t } = useTranslation()

    return (rule: string) => {
        const folder = ruleFolder(rule)

        if (folder) {
            return t('All pages in {{folder}}', { folder })
        }

        if (rule.includes('*')) {
            return rule
        }

        return documents.find((document) => pageRule(document.url) === rule)?.title ?? rule
    }
}

/**
 * Whether the Vacancy column shows: once the website has a vacancy or a rule about vacancies. Before that, a page is
 * marked as a vacancy from its menu, so websites without vacancies do not get a column of switches that are all off.
 */
export function useShowsVacancySwitch(siteId: string): boolean {
    const { data: site } = useSite(siteId)

    // Only websites Evaa reads itself have rules.
    const { data: settings } = useCrawlSettings(siteId, site?.type === 'snippet')

    return (
        (site?.vacanciesCount ?? 0) > 0 ||
        (settings?.vacancyPaths.length ?? 0) > 0 ||
        (settings?.notVacancyPaths.length ?? 0) > 0
    )
}

type RuleChoiceProps = {
    title: string
    description?: string
    document: SiteDocument
    onChoose: (rule: string) => void
}

/** "Only this page" or "All pages in /vacatures/, also new ones": the two ways a page can get a rule. */
function RuleChoice({ title, description, document, onChoose }: RuleChoiceProps) {
    const { t } = useTranslation()
    const folder = folderOf(document.url)

    return (
        <Flex
            vertical
            gap="small"
            className="max-w-72"
        >
            <Typography.Text strong>{title}</Typography.Text>
            {description && <Typography.Text type="secondary">{description}</Typography.Text>}
            <Button
                block
                onClick={() => onChoose(pageRule(document.url))}
            >
                {t('Only this page')}
            </Button>
            {folder && (
                <Button
                    block
                    onClick={() => onChoose(folderRule(folder))}
                    className="h-auto py-1 whitespace-normal"
                >
                    {t('All pages in {{folder}}, also new ones', { folder })}
                </Button>
            )}
        </Flex>
    )
}

type RuleControlProps = {
    rules: CrawlRules
    document: SiteDocument
}

/**
 * Whether a page is a vacancy, as a switch. On or off asks for this page or its whole folder, and switching back
 * removes the rule that did it. Off also works for a vacancy Evaa recognised itself, for a vacancy Evaa should not
 * recommend. The question is only mounted while it is asked, so a long table stays light.
 */
export const VacancySwitch = memo(function VacancySwitch({ rules, document }: RuleControlProps) {
    const [asking, setAsking] = useState(false)
    const checked = !document.notVacancyRule && (document.kind === 'vacancy' || !!document.vacancyRule)

    // The rule that made the page what it is now, which switching undoes.
    const rule = checked ? document.vacancyRule : document.notVacancyRule
    const undo = checked ? rules.removeVacancy : rules.removeNotVacancy
    const add = checked ? rules.addNotVacancy : rules.addVacancy

    // A rule for more pages than this one is confirmed first; without a rule, Evaa asks for this page or its folder.
    const widerRule = rule && rule !== pageRule(document.url) ? rule : null
    const asksFirst = widerRule || (!rule && folderOf(document.url))

    const toggle = () => {
        if (asksFirst) {
            setAsking(true)
        } else if (rule) {
            undo(rule)
        } else {
            add(pageRule(document.url))
        }
    }

    const toggleSwitch = (
        <Switch
            size="small"
            checked={checked}
            onChange={toggle}
        />
    )

    if (!asking) {
        return toggleSwitch
    }

    if (widerRule) {
        return (
            <VacancyRuleConfirm
                rule={widerRule}
                checked={checked}
                onRemove={() => {
                    setAsking(false)
                    undo(widerRule)
                }}
                onClose={() => setAsking(false)}
            >
                {toggleSwitch}
            </VacancyRuleConfirm>
        )
    }

    return (
        <VacancyRuleChoice
            document={document}
            checked={checked}
            onChoose={(chosen) => {
                setAsking(false)
                add(chosen)
            }}
            onClose={() => setAsking(false)}
        >
            {toggleSwitch}
        </VacancyRuleChoice>
    )
})

type VacancyRuleConfirmProps = {
    rule: string
    /** Whether the page is a vacancy now. */
    checked: boolean
    onRemove: () => void
    onClose: () => void
    children: React.ReactNode
}

/** Asks to remove a rule for more pages than the one switched. */
function VacancyRuleConfirm({ rule, checked, onRemove, onClose, children }: VacancyRuleConfirmProps) {
    const { t } = useTranslation()

    const description = checked
        ? t('Remove this rule? Then these pages are no longer vacancies.')
        : t('Remove this rule? Then Evaa recognises vacancies on these pages again.')

    return (
        <Popconfirm
            open
            onOpenChange={(open) => !open && onClose()}
            title={
                <VacancyRuleTitle
                    rule={rule}
                    checked={checked}
                />
            }
            description={description}
            okText={t('Remove')}
            cancelText={t('Cancel')}
            onConfirm={onRemove}
            onCancel={onClose}
        >
            {children}
        </Popconfirm>
    )
}

/** "All pages in /vacatures/ are vacancies" for a folder rule, or the rule as it was typed. */
function VacancyRuleTitle({ rule, checked }: Pick<VacancyRuleConfirmProps, 'rule' | 'checked'>) {
    const { t } = useTranslation()
    const folder = ruleFolder(rule)

    if (!folder) {
        return rule
    }

    return checked
        ? t('All pages in {{folder}} are vacancies', { folder })
        : t('All pages in {{folder}} are not vacancies', { folder })
}

type VacancyRuleChoiceProps = {
    document: SiteDocument
    /** Whether the page is a vacancy now. */
    checked: boolean
    onChoose: (rule: string) => void
    onClose: () => void
    children: React.ReactNode
}

/** Asks whether the switch is for this page or for its whole folder. */
function VacancyRuleChoice({ document, checked, onChoose, onClose, children }: VacancyRuleChoiceProps) {
    const { t } = useTranslation()
    const title = checked ? t('Which pages are not vacancies?') : t('Which pages are vacancies?')

    const description = checked
        ? t('Evaa then does not recommend them as vacancies. To keep Evaa from using a page at all, skip it.')
        : undefined

    return (
        <Popover
            open
            onOpenChange={(open) => !open && onClose()}
            trigger="click"
            content={
                <RuleChoice
                    title={title}
                    description={description}
                    document={document}
                    onChoose={onChoose}
                />
            }
        >
            {children}
        </Popover>
    )
}

type PageMenuProps = {
    rules: CrawlRules
    document: SiteDocument
    // Without the Vacancy column, marking a page as a vacancy is in the menu.
    withMarkAsVacancy: boolean
    reading: boolean
    onReadAgain: () => void
}

/**
 * Read a page again, and, while the website has no vacancies, mark it as one. Marking asks for this page or its whole
 * folder; the question is only mounted while it is asked.
 */
export const PageMenu = memo(function PageMenu({
    rules,
    document,
    withMarkAsVacancy,
    reading,
    onReadAgain,
}: PageMenuProps) {
    const { t } = useTranslation()
    const [asking, setAsking] = useState(false)

    const markAsVacancy = {
        key: 'vacancy',
        label: t('This is a vacancy'),
    }

    const actions = [
        {
            key: 'read',
            label: t('Read again'),
            icon: <ReloadOutlined />,
        },
        ...(withMarkAsVacancy ? [markAsVacancy] : []),
    ]

    const items: MenuProps['items'] = document.fetchedAt
        ? [
              {
                  key: 'last-read',
                  type: 'group',
                  label: (
                      <>
                          {t('Last read')} <TimeAgo date={document.fetchedAt} />
                      </>
                  ),
                  children: actions,
              },
          ]
        : actions

    const choose = (key: string) => {
        if (key === 'read') {
            onReadAgain()
        } else {
            setAsking(true)
        }
    }

    const trigger = (
        <Dropdown
            trigger={['click']}
            menu={{
                items,
                onClick: ({ key }) => choose(key),
            }}
        >
            <Button
                size="small"
                type="text"
                icon={<MoreOutlined />}
                loading={reading}
                aria-label={t('More')}
            />
        </Dropdown>
    )

    if (!asking) {
        return trigger
    }

    return (
        <Popover
            open
            onOpenChange={(open) => !open && setAsking(false)}
            trigger="click"
            placement="bottomRight"
            content={
                <RuleChoice
                    title={t('Which pages are vacancies?')}
                    document={document}
                    onChoose={(rule) => {
                        rules.addVacancy(rule)
                        setAsking(false)
                    }}
                />
            }
        >
            {trigger}
        </Popover>
    )
})

/**
 * Whether Evaa reads a page. Unticking skips only this page; ticking removes the rule that skips it, which is confirmed
 * first when that rule skips more pages.
 */
export const PageCheckbox = memo(function PageCheckbox({ rules, document }: RuleControlProps) {
    const { t } = useTranslation()
    const skipped = document.status === 'skipped'
    const rule = skipped ? document.skipRule : null
    const widerRule = rule && rule !== pageRule(document.url) ? rule : null

    const toggle = () => {
        if (!skipped) {
            rules.skipPage(document.url)
        } else if (rule && !widerRule) {
            rules.removeSkip(rule)
        }
    }

    const checkbox = (
        <Checkbox
            checked={!skipped}
            aria-label={t('Evaa reads this page')}
            onChange={toggle}
        />
    )

    if (!widerRule) {
        return checkbox
    }

    const folder = ruleFolder(widerRule)

    return (
        <Popconfirm
            title={folder ? t('All pages in {{folder}} are skipped', { folder }) : widerRule}
            description={t('Read them all again?')}
            okText={t('Read again')}
            cancelText={t('Cancel')}
            placement="bottomLeft"
            onConfirm={() => rules.removeSkip(widerRule)}
        >
            {checkbox}
        </Popconfirm>
    )
})

type FolderCheckboxProps = {
    rules: CrawlRules
    folder: string
    pages: number
    skipped: number
}

/**
 * Whether Evaa reads the pages in a folder: ticked when it reads all of them, half when it skips some. Unticking skips
 * the whole folder, new pages in it too, after a question; ticking reads every page in it again.
 */
export const FolderCheckbox = memo(function FolderCheckbox({ rules, folder, pages, skipped }: FolderCheckboxProps) {
    const { t } = useTranslation()

    if (skipped > 0) {
        return (
            <Checkbox
                checked={false}
                indeterminate={skipped < pages}
                aria-label={t('Evaa reads the pages in {{folder}}', { folder })}
                onChange={() => rules.readFolder(folder)}
            />
        )
    }

    return (
        <Popconfirm
            title={t('Skip all pages in {{folder}}?', { folder })}
            description={t('Evaa skips the {{count}} pages in this folder, and new pages in it too.', { count: pages })}
            okText={t('Skip')}
            cancelText={t('Cancel')}
            placement="bottomLeft"
            onConfirm={() => rules.skipFolder(folder)}
        >
            <Checkbox
                checked
                aria-label={t('Evaa reads the pages in {{folder}}', { folder })}
            />
        </Popconfirm>
    )
})

type SiteCrawlRulesProps = {
    rules: CrawlRules
    documents: SiteDocument[]
}

/**
 * The rules of a website in plain words above its pages: which pages are vacancies and which Evaa skips, each with a
 * way to undo it.
 */
export function SiteCrawlRules({ rules, documents }: SiteCrawlRulesProps) {
    const { t } = useTranslation()
    const label = useRuleLabel(documents)
    const vacancyPaths = rules.settings?.vacancyPaths ?? []
    const notVacancyPaths = rules.settings?.notVacancyPaths ?? []

    // Skipped pages show in the table; only the rules for pages Evaa never found show here.
    const exclude = (rules.settings?.exclude ?? []).filter(
        (rule) => !documents.some((document) => document.skipRule === rule)
    )

    const hasNoRules = vacancyPaths.length === 0 && notVacancyPaths.length === 0 && exclude.length === 0

    if (hasNoRules) {
        return null
    }

    const removable = (rule: string, onRemove: (rule: string) => void) => (
        <Tag
            key={rule}
            closable
            onClose={(event) => {
                event.preventDefault()
                onRemove(rule)
            }}
            className="m-0"
        >
            {label(rule)}
        </Tag>
    )

    return (
        <Flex
            vertical
            gap="small"
        >
            {vacancyPaths.length > 0 && (
                <RuleRow title={t('Vacancies')}>
                    {vacancyPaths.map((rule) => removable(rule, rules.removeVacancy))}
                </RuleRow>
            )}
            {notVacancyPaths.length > 0 && (
                <RuleRow title={t('Not a vacancy')}>
                    {notVacancyPaths.map((rule) => removable(rule, rules.removeNotVacancy))}
                </RuleRow>
            )}
            {exclude.length > 0 && (
                <RuleRow title={t('Skipped')}>
                    {exclude.map((rule) => (
                        <Tag
                            key={rule}
                            className="m-0"
                        >
                            {label(rule)}{' '}
                            <Typography.Link onClick={() => rules.removeSkip(rule)}>{t('Read again')}</Typography.Link>
                        </Tag>
                    ))}
                </RuleRow>
            )}
        </Flex>
    )
}

type RuleRowProps = {
    title: string
    children: React.ReactNode
}

function RuleRow({ title, children }: RuleRowProps) {
    return (
        <Flex
            gap="small"
            align="center"
            wrap
        >
            <Typography.Text type="secondary">{title}:</Typography.Text>
            {children}
        </Flex>
    )
}
